import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import InlineFeedback from './components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '::1' || currentHost === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;

const ServiceList = () => {
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let isActive = true;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [servicesRes, categoriesRes] = await Promise.all([
          axios.get(apiUrl('services/'), { headers }),
          axios.get(apiUrl('categories/'), { headers }),
        ]);

        const serviceRows = Array.isArray(servicesRes.data)
          ? servicesRes.data
          : Array.isArray(servicesRes.data?.results)
            ? servicesRes.data.results
            : [];

        const categoryRows = Array.isArray(categoriesRes.data)
          ? categoriesRes.data
          : Array.isArray(categoriesRes.data?.results)
            ? categoriesRes.data.results
            : [];

        if (isActive) {
          setServices(serviceRows);
          setCategories(categoryRows);
          setMessage('');
        }
      } catch (error) {
        console.error('Error fetching services page data:', error?.response?.data || error.message);
        if (isActive) {
          setMessage('❌ Unable to load services right now. Please try again.');
          setServices([]);
          setCategories([]);
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      isActive = false;
    };
  }, []);

  const getWorkerIdFromService = (service) => {
    if (typeof service?.worker === 'number' || typeof service?.worker === 'string') return service.worker;
    if (typeof service?.worker === 'object' && service?.worker?.id) return service.worker.id;

    if (typeof service?.created_by === 'number' || typeof service?.created_by === 'string') return service.created_by;
    if (typeof service?.created_by === 'object' && service?.created_by?.id) return service.created_by.id;

    return '';
  };

  const handleServiceClick = (service) => {
    const serviceId = service?.id;
    const workerId = getWorkerIdFromService(service);
    const bookingUrl = `/booking?serviceId=${encodeURIComponent(serviceId || '')}&workerId=${encodeURIComponent(workerId || '')}&fromServices=1`;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: bookingUrl } });
      return;
    }

    navigate(bookingUrl);
  };

  const categoryOptions = useMemo(() => {
    const byName = (categories || []).map((category) => ({
      id: String(category.id),
      label: category.name,
    }));
    return [{ id: 'all', label: 'All Categories' }, ...byName];
  }, [categories]);

  const filteredServices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    const filtered = (services || []).filter((service) => {
      const categoryId = typeof service?.category === 'object' ? String(service.category?.id) : String(service?.category || '');
      const categoryMatch = selectedCategory === 'all' || categoryId === selectedCategory;

      const haystack = [service?.name, service?.description, service?.category_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const searchMatch = !query || haystack.includes(query);
      return categoryMatch && searchMatch;
    });

    return [...filtered].sort((left, right) => {
      if (sortBy === 'price_low') return Number(left?.price || 0) - Number(right?.price || 0);
      if (sortBy === 'price_high') return Number(right?.price || 0) - Number(left?.price || 0);
      if (sortBy === 'duration') return Number(left?.duration || 0) - Number(right?.duration || 0);
      if (sortBy === 'name') return String(left?.name || '').localeCompare(String(right?.name || ''));
      const leftRating = Number(left?.average_rating || 0);
      const rightRating = Number(right?.average_rating || 0);
      return rightRating - leftRating;
    });
  }, [services, searchQuery, selectedCategory, sortBy]);

  const topRatedCount = useMemo(
    () => services.filter((service) => Number(service?.average_rating || 0) >= 4.5).length,
    [services]
  );

  const feedbackType = message.startsWith('❌') ? 'error' : 'info';

  return (
    <div className="min-h-screen bg-[#F6F4F8] px-3 py-4 text-[#2E2E2E] sm:px-4 sm:py-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="rounded-2xl bg-gradient-to-r from-[#6D3B7E] to-[#8A4AA3] p-4 text-white shadow-lg sm:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">Services</h1>
              <p className="mt-2 text-sm text-purple-100 md:text-base">
                Discover your next style and jump directly into booking with one click.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="rounded-lg bg-white/15 px-4 py-2 text-sm font-semibold text-white backdrop-blur hover:bg-white/25"
              >
                🏠 Home
              </button>
              <button
                type="button"
                onClick={() => navigate('/booking')}
                className="rounded-lg bg-[#CDA349] px-4 py-2 text-sm font-semibold text-white hover:bg-[#b8913e]"
              >
                📅 Go To Booking
              </button>
            </div>
          </div>
        </header>

        {message ? <InlineFeedback type={feedbackType} message={message} /> : null}
        {isLoading ? <InlineFeedback type="info" message="Loading services..." /> : null}

        <section className="rounded-2xl bg-white p-4 shadow-sm">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-[#F4EBF8] px-3 py-1.5 text-xs font-semibold text-[#6D3B7E]">
              Total services: {services.length}
            </span>
            <span className="rounded-full bg-[#E8F7EF] px-3 py-1.5 text-xs font-semibold text-[#0F766E]">
              Top rated (4.5+): {topRatedCount}
            </span>
            <span className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700">
              Showing: {filteredServices.length}
            </span>
          </div>
        </section>

        <section className="rounded-2xl bg-white p-4 shadow-sm">
          <div className="grid gap-3 lg:grid-cols-[1fr_auto_auto] lg:items-end">
            <div>
              <label htmlFor="service-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                Search
              </label>
              <input
                id="service-search"
                type="text"
                placeholder="Search by service name, description, or category"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label htmlFor="service-category" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                Category
              </label>
              <select
                id="service-category"
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                {categoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>{category.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="service-sort" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                Sort
              </label>
              <select
                id="service-sort"
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="featured">Featured</option>
                <option value="name">Name</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="duration">Duration</option>
              </select>
            </div>
          </div>
        </section>

        {!isLoading && filteredServices.length === 0 ? (
          <section className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center shadow-sm">
            <h2 className="text-xl font-semibold text-[#6D3B7E]">No services found</h2>
            <p className="mt-2 text-sm text-gray-600">Try another search term or category filter.</p>
          </section>
        ) : null}

        <section className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {filteredServices.map((service) => {
            const rating = Number(service?.average_rating || 0);
            const isFeatured = rating >= 4.8;

            return (
              <article
                key={service.id}
                className="overflow-hidden rounded-2xl border border-transparent bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-[#E5D4ED] hover:shadow-md"
              >
                <div className="relative aspect-[16/10] bg-gradient-to-br from-[#EEDFF4] to-[#D7BDE2]">
                  {service.image_data ? (
                    <img src={service.image_data} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-4xl text-[#6D3B7E]" aria-hidden="true">✦</div>
                  )}
                  {isFeatured ? (
                    <span className="absolute right-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-amber-700 shadow-sm">Featured</span>
                  ) : null}
                </div>

                <div className="p-5">
                  <h2 className="text-lg font-semibold text-[#4B2A56]">{service.name}</h2>

                  <p className="mt-2 text-sm text-gray-600">{service.description || 'Professional beauty service with personalized care.'}</p>

                  <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold">
                    <span className="rounded-full bg-[#F4EBF8] px-2.5 py-1 text-[#6D3B7E]">
                      ${Number(service?.price || 0).toFixed(2)}
                    </span>
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-700">
                      {Number(service?.duration || 0)} mins
                    </span>
                    {service?.category_name ? (
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-700">
                        {service.category_name}
                      </span>
                    ) : null}
                    {rating > 0 ? (
                      <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-700">
                        ⭐ {rating.toFixed(1)}
                      </span>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleServiceClick(service)}
                    className="mt-5 min-h-11 w-full rounded-full bg-[#6D3B7E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5a326a]"
                  >
                    Select and continue to booking
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      </div>
    </div>
  );
};

export default ServiceList;
